<template>
  <div class="registration-layout">
    <registration-header @close="onCloseClicked" :showCloseButton="ableToClose" :title="$t('registration.sp.freelancer')"/>
    <div class="content-area">
      <router-view />
    </div>
    <registration-stepper
      :currentStep="spFreelancerRegistrationCurrentStep"
      :filledStepsCnt="spFreelancerRegistrationFilledStepsCnt"
      :visitedStepsCnt="spFreelancerRegistrationVisitedStepsCnt"
      :stepNames="stepNames"
      :loadingNext="loadingNext"
      :allowNext="allowNext"
      @clicked="onChangeStep"
    />
    <yes-no-popup
      :visible-popup="showCloseConfirmationPopup"
      title="registration.close.title"
      message="registration.close.message"
      yesCaption="confirm.ok"
      noCaption="confirm.cancel"
      @clicked="onClickYesNoButton"
    />
    <error-message :errorText="errorMessage" @close="errorMessage = ''" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  CLEAN_SP_FREELANCER_REGISTRATION_DATA,
  CHANGE_SP_FREELANCER_REGISTRATION_CURRENT_STEP,
  CHANGE_SP_FREELANCER_REGISTRATION_RECAPTCHA_TOKEN,
  CHANGE_SP_FREELANCER_REGISTRATION_FILLED_STEPS_COUNT,
  REGISTER_SP_FREELANCER, LOAD_SERVICES, LOAD_CERTIFICATES,
  CHANGE_SP_FREELANCER_REGISTRATION_VISITED_STEPS_COUNT,
} from '@/store/spFreelancerRegistration/actionTypes';

import { views, SP_FREELANCER_REGISTRATION_ROUTE_MAP } from '@/router/constants';

import YesNoPopup from '@/components/popup/YesNoPopup.vue';
import ErrorMessage from '@/components/common/ErrorMessage.vue';
import RegistrationHeader from '@/components/registration/RegistrationHeader.vue';
import RegistrationStepper from '@/components/registration/RegistrationStepper.vue';
import { loadRecaptcha } from '@/utils/recaptcha-loader';

export default {
  name: 'SPFreelancerRegistrationLayout',
  components: {
    YesNoPopup,
    ErrorMessage,
    RegistrationHeader,
    RegistrationStepper,
  },
  data() {
    return {
      showCloseConfirmationPopup: false,
      errorMessage: '',
      loadingNext: false,
    };
  },
  async mounted() {
    // Load reCAPTCHA dynamically only for registration
    await loadRecaptcha();

    this.$store.dispatch(LOAD_SERVICES);
    this.$store.dispatch(LOAD_CERTIFICATES);
  },
  computed: {
    stepNames() {
      return [
        'registration.sp.freelancer.email.verification',
        'registration.sp.freelancer.client.info',
        'registration.sp.freelancer.company.services',
        'registration.sp.freelancer.company.certification',
        'registration.sp.freelancer.registration.finish',
      ];
    },
    ...mapGetters([
      'spFreelancerServices',
      'allowNextFirstStepSPFreelancer',
      'allowNextSecondStepSPFreelancer',
      'allowNextThirdStepSPFreelancer',
      'allowNextForthStepSPFreelancer',
      'spFreelancerRegistrationEmailVerificationInfo',
      'spFreelancerRegistrationClientInfo',
      'spFreelancerRegistrationCurrentStep',
      'spFreelancerRegistrationFilledStepsCnt',
      'spFreelancerRegistrationVisitedStepsCnt',
    ]),
    allowNext() {
      if (this.spFreelancerRegistrationCurrentStep === 1) {
        return this.allowNextFirstStepSPFreelancer;
      }
      if (this.spFreelancerRegistrationCurrentStep === 2) {
        return this.allowNextSecondStepSPFreelancer;
      }
      if (this.spFreelancerRegistrationCurrentStep === 3) {
        return this.allowNextThirdStepSPFreelancer;
      }
      if (this.spFreelancerRegistrationCurrentStep === 4) {
        return this.allowNextForthStepSPFreelancer;
      }
      return true;
    },
    ableToClose() {
      return this.spFreelancerRegistrationCurrentStep !== 5;
    },
  },
  methods: {
    async getRecaptchaToken() {
      await this.$recaptchaLoaded();
      this.recaptchaUserToken = await this.$recaptcha('login');
      this.$recaptchaInstance.showBadge();

      if (this.recaptchaUserToken) {
        await this.$store.dispatch(CHANGE_SP_FREELANCER_REGISTRATION_RECAPTCHA_TOKEN, this.recaptchaUserToken);
      } else {
        this.errorText = this.$t('registration.client.info.recaptcha.is.empty.or.not.valid');
      }
    },

    async onChangeStep(stepNumber) {
      if (stepNumber === 2) {
        await this.getRecaptchaToken();
      }
      if (stepNumber === 5) {
        this.loadingNext = true;
        await this.getRecaptchaToken();
        try {
          await this.$store.dispatch(REGISTER_SP_FREELANCER);
        } catch (e) {
          this.errorMessage = e.message;
          throw e;
        } finally {
          this.loadingNext = false;
        }
      }

      await this.$store.dispatch(CHANGE_SP_FREELANCER_REGISTRATION_CURRENT_STEP, Number(stepNumber));
      await this.$store.dispatch(CHANGE_SP_FREELANCER_REGISTRATION_FILLED_STEPS_COUNT, 1);
      if (stepNumber > this.spFreelancerRegistrationVisitedStepsCnt) {
        await this.$store.dispatch(CHANGE_SP_FREELANCER_REGISTRATION_VISITED_STEPS_COUNT, Number(stepNumber));
      }
    },
    onCloseClicked() {
      this.showCloseConfirmationPopup = true;
    },
    async onClickYesNoButton(result) {
      this.showCloseConfirmationPopup = false;

      if (result) {
        await this.$store.dispatch(CLEAN_SP_FREELANCER_REGISTRATION_DATA);
        await this.$router.replace({ name: views.SIGN_IN });
      }
    },
  },
  watch: {
    spFreelancerRegistrationCurrentStep: {
      handler() {
        this.$router.replace(SP_FREELANCER_REGISTRATION_ROUTE_MAP[this.spFreelancerRegistrationCurrentStep]);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.registration-layout {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
  background-color: var(--bg-primary);
}

.content-area {
  margin-bottom: rem(72);
  padding: 0 rem(192);
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding-top: rem(30);
}

.registration-stepper {
  position: fixed;
  z-index: 1;
  background-color: $white;
  bottom: 0;
  width: 100%;
  padding: rem(10) rem(116);
  box-shadow: $shadow-md;
}
</style>
